import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  useWatch,
} from 'react-hook-form';
import SharedEmailAutocomplete from '../../../../shared/shared-email-autocomplete';
import OrganizationsAutoComplete from '../../../../shared/organizations-autocomplete';
import {
  VAULT_EXPORT_REGION_OPTIONS,
  AttendeeResponse,
} from '../../../../../types';
import VaultTooltipWithLink from '../../../automation/decommissioning/decom-policy-form/form-steps/shared/vault-tooltip-with-link';
import { CreateVaultExportFormValues } from '../create-vault-export-form';
import {
  dataScopeOptions,
  searchMethodOptions,
  searchMethodLabelMap,
  timeZones,
} from '../vault-export-constants';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

interface CreateCalendarVaultExportProps {
  control: Control<CreateVaultExportFormValues>;
  errors: FieldErrors<CreateVaultExportFormValues>;
  register: UseFormRegister<CreateVaultExportFormValues>;
  selectedSearchMethod: string;
  exportFormatOptions: { value: string; label: string; disabled: boolean }[];
}

export default function CreateCalendarVaultExport({
  control,
  errors,
  register,
  selectedSearchMethod,
  exportFormatOptions,
}: CreateCalendarVaultExportProps) {
  const theme = useTheme();
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const [isExportSettingsExpanded, setIsExportSettingsExpanded] =
    useState(false);
  const dataScope = useWatch({ control, name: 'dataScope' });
  const isHeldData = dataScope === 'HELD_DATA';

  const handleToggleResponseStatus = (
    status: string,
    selected: string[],
    onChange: (value: string[]) => void
  ) => {
    if (selected.includes(status)) {
      onChange(selected.filter((s) => s !== status));
    } else {
      onChange([...selected, status]);
    }
  };

  return (
    <>
      <Controller
        name="dataScope"
        control={control}
        render={({ field }) => (
          <FormControl sx={{ width: '50%' }} size="small">
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Source
            </Typography>
            <TextField
              {...field}
              select
              required
              error={!!errors.dataScope}
              helperText={errors?.dataScope?.message || ' '}
            >
              {dataScopeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={
                    option.disabled || option.value === 'UNPROCESSED_DATA'
                  }
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        )}
      />

      <Controller
        name="searchMethod"
        control={control}
        render={({ field }) => (
          <FormControl sx={{ width: '50%' }} size="small">
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Entity
            </Typography>
            <TextField
              {...field}
              select
              required
              error={!!errors.searchMethod}
              helperText={errors?.searchMethod?.message || ' '}
            >
              {searchMethodOptions
                .filter((option) => option.value !== 'SHARED_DRIVE')
                .map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </MenuItem>
                ))}
            </TextField>
          </FormControl>
        )}
      />

      {selectedSearchMethod !== 'ENTIRE_ORG' &&
        (selectedSearchMethod === 'ACCOUNT' ? (
          <Controller
            name="searchMethodTarget"
            control={control}
            render={({ field }) => {
              const selectedEmails = field.value
                ? field.value.split(',').filter(Boolean)
                : [];
              return (
                <FormControl sx={{ width: '100%' }}>
                  <SharedEmailAutocomplete
                    freeSolo
                    sendType="Account Email Addresses"
                    sources={['users']}
                    multiAutoCompleteValue={selectedEmails}
                    setMultiAutoCompleteValue={(newEmails) => {
                      field.onChange(newEmails.join(','));
                    }}
                    errors={!!errors.searchMethodTarget}
                    helperText={errors?.searchMethodTarget?.message || ' '}
                    required
                    renderChipsBelow={true}
                  />
                </FormControl>
              );
            }}
          />
        ) : selectedSearchMethod === 'ORG_UNIT' ? (
          <Controller
            name="searchMethodTarget"
            control={control}
            render={({ field }) => (
              <FormControl sx={{ width: '50%' }}>
                <OrganizationsAutoComplete
                  autoCompleteValue={field.value || ''}
                  setAutoCompleteValue={field.onChange}
                  size="small"
                  hasError={!!errors.searchMethodTarget}
                  helperText={errors?.searchMethodTarget?.message || ' '}
                  disableParents={true}
                />
              </FormControl>
            )}
          />
        ) : (
          <Controller
            name="searchMethodTarget"
            control={control}
            render={({ field }) => (
              <FormControl sx={{ width: '50%' }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 'bold' }}
                >
                  {searchMethodLabelMap[selectedSearchMethod] || 'Target ID'}
                </Typography>
                <TextField
                  {...field}
                  size="small"
                  required
                  placeholder={'Enter target here'}
                  error={!!errors.searchMethodTarget}
                  helperText={errors?.searchMethodTarget?.message || ' '}
                />
              </FormControl>
            )}
          />
        ))}

      <Stack direction="row" gap={2} sx={{ width: '100%' }}>
        {!isHeldData && (
          <Controller
            name="timeZone"
            control={control}
            render={({ field }) => (
              <FormControl sx={{ width: '50%' }} size="small">
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Timezone
                  </Typography>
                  <VaultTooltipWithLink
                    preset="vaultTimezone"
                    sxIcon={{ mb: 0.5 }}
                  />
                </Stack>
                <TextField {...field} select required error={!!errors.timeZone}>
                  {timeZones.map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {tz}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            )}
          />
        )}
        <Controller
          name="startTime"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl sx={{ width: isHeldData ? '50%' : '25%' }}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 'bold' }}
                >
                  Event Date (optional)
                </Typography>
                <Tooltip
                  title="Only events within this date range will be exported."
                  arrow
                >
                  <InfoOutlined
                    fontSize="small"
                    color="action"
                    sx={{ mb: 0.5 }}
                  />
                </Tooltip>
              </Stack>
              <DatePicker
                value={value ? dayjs(value) : null}
                onChange={(newValue) => {
                  if (newValue === null) {
                    onChange('');
                  } else if (newValue.isValid()) {
                    onChange(newValue.format('YYYY-MM-DD'));
                  } else {
                    onChange('invalid');
                  }
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!errors.startTime,
                    helperText: errors?.startTime?.message || ' ',
                  },
                }}
              />
            </FormControl>
          )}
        />
        <Controller
          name="endTime"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl sx={{ width: isHeldData ? '50%' : '25%', mt: 3.5 }}>
              <DatePicker
                value={value ? dayjs(value) : null}
                onChange={(newValue) => {
                  if (newValue === null) {
                    onChange('');
                  } else if (newValue.isValid()) {
                    onChange(newValue.format('YYYY-MM-DD'));
                  } else {
                    onChange('invalid');
                  }
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!errors.endTime,
                    helperText: errors?.endTime?.message || ' ',
                  },
                }}
              />
            </FormControl>
          )}
        />
      </Stack>

      <Controller
        name="methodTargetVersionDate"
        control={control}
        render={({ field: { onChange, value } }) => (
          <FormControl sx={{ width: '50%' }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Version Date (optional)
              </Typography>
              <VaultTooltipWithLink
                preset="calendarEventVersioning"
                sxIcon={{ mb: 0.5 }}
              />
            </Stack>
            <DatePicker
              value={value ? dayjs(value) : null}
              onChange={(newValue) => {
                if (newValue === null) {
                  onChange('');
                } else if (newValue.isValid()) {
                  onChange(newValue.format('YYYY-MM-DD'));
                } else {
                  onChange('invalid');
                }
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: !!errors.methodTargetVersionDate,
                  helperText: errors?.methodTargetVersionDate?.message || ' ',
                },
              }}
            />
          </FormControl>
        )}
      />

      <Box sx={{ width: '50%' }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Terms (optional)
          </Typography>
          <VaultTooltipWithLink
            preset="calendarQueryTerms"
            sxIcon={{ mb: 0.5 }}
          />
        </Stack>
        <TextField
          {...register('terms')}
          placeholder="Query Terms"
          size="small"
          variant="standard"
          helperText="Example: from:username1 subject: Hello has:attachment"
          fullWidth
          error={!!errors.terms}
        />
      </Box>

      <Accordion
        elevation={0}
        disableGutters
        expanded={isAdvancedExpanded}
        onChange={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
        sx={{
          '&:before': { display: 'none' },
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          width: '100%',
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMore sx={{ color: theme.palette.colors?.primary?.[600] }} />
          }
          sx={{
            px: 0,
            minHeight: 'auto',
            width: 'fit-content',
            backgroundColor: 'transparent !important',
            '& .MuiAccordionSummary-content': { my: 0.5, flexGrow: 0, mr: 1 },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: theme.palette.colors?.primary?.[600],
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {isAdvancedExpanded
              ? 'Hide Advanced Options'
              : 'Show Advanced Options'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: '50%',
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Who (optional)
            </Typography>
            <Controller
              name="peopleQuery"
              control={control}
              render={({ field }) => (
                <Box>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value || []}
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Enter guests, creators, or organizers"
                        size="small"
                        fullWidth
                        variant="standard"
                      />
                    )}
                  />
                  {field.value && field.value.length > 0 && (
                    <Stack
                      direction="row"
                      gap={1}
                      flexWrap="wrap"
                      sx={{ mt: 1, mb: 0.5 }}
                    >
                      {field.value.map((val: string, index: number) => (
                        <Chip
                          key={`${val}-${index}`}
                          variant="outlined"
                          label={val}
                          size="small"
                          onDelete={() => {
                            const newVals = (field.value || []).filter(
                              (_: string, i: number) => i !== index
                            );
                            field.onChange(newVals);
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                    Press Enter to separate multiple users. Example:
                    user@domain.com
                  </FormHelperText>
                </Box>
              )}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Where (optional)
            </Typography>
            <Controller
              name="locationQuery"
              control={control}
              render={({ field }) => (
                <Box>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value || []}
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Enter location or rooms"
                        size="small"
                        fullWidth
                        variant="standard"
                      />
                    )}
                  />
                  {field.value && field.value.length > 0 && (
                    <Stack
                      direction="row"
                      gap={1}
                      flexWrap="wrap"
                      sx={{ mt: 1, mb: 0.5 }}
                    >
                      {field.value.map((val: string, index: number) => (
                        <Chip
                          key={`${val}-${index}`}
                          variant="outlined"
                          label={val}
                          size="small"
                          onDelete={() => {
                            const newVals = (field.value || []).filter(
                              (_: string, i: number) => i !== index
                            );
                            field.onChange(newVals);
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                    Press Enter to separate multiple locations or rooms
                  </FormHelperText>
                </Box>
              )}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Doesn't have (optional)
            </Typography>
            <Controller
              name="minusWords"
              control={control}
              render={({ field }) => (
                <Box>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={field.value || []}
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Keywords not contained in the title, description, guests, locations, or rooms"
                        size="small"
                        fullWidth
                        variant="standard"
                      />
                    )}
                  />
                  {field.value && field.value.length > 0 && (
                    <Stack
                      direction="row"
                      gap={1}
                      flexWrap="wrap"
                      sx={{ mt: 1, mb: 0.5 }}
                    >
                      {field.value.map((val: string, index: number) => (
                        <Chip
                          key={`${val}-${index}`}
                          variant="outlined"
                          label={val}
                          size="small"
                          onDelete={() => {
                            const newVals = (field.value || []).filter(
                              (_: string, i: number) => i !== index
                            );
                            field.onChange(newVals);
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                    Press Enter to separate multiple terms
                  </FormHelperText>
                </Box>
              )}
            />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              Response status (optional)
              <VaultTooltipWithLink preset="calendarResponseStatus" />
            </Typography>
            <Controller
              name="responseStatuses"
              control={control}
              render={({ field }) => {
                const selected = field.value || [];
                return (
                  <Stack direction="row" gap={3} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.includes(AttendeeResponse.ACCEPTED)}
                          onChange={() =>
                            handleToggleResponseStatus(
                              AttendeeResponse.ACCEPTED,
                              selected,
                              field.onChange
                            )
                          }
                        />
                      }
                      label={<Typography variant="body2">Yes</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.includes(AttendeeResponse.DECLINED)}
                          onChange={() =>
                            handleToggleResponseStatus(
                              AttendeeResponse.DECLINED,
                              selected,
                              field.onChange
                            )
                          }
                        />
                      }
                      label={<Typography variant="body2">No</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.includes(
                            AttendeeResponse.TENTATIVE
                          )}
                          onChange={() =>
                            handleToggleResponseStatus(
                              AttendeeResponse.TENTATIVE,
                              selected,
                              field.onChange
                            )
                          }
                        />
                      }
                      label={<Typography variant="body2">Maybe</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.includes(
                            AttendeeResponse.NEEDS_ACTION
                          )}
                          onChange={() =>
                            handleToggleResponseStatus(
                              AttendeeResponse.NEEDS_ACTION,
                              selected,
                              field.onChange
                            )
                          }
                        />
                      }
                      label={
                        <Typography variant="body2">No response</Typography>
                      }
                    />
                  </Stack>
                );
              }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        elevation={0}
        disableGutters
        expanded={isExportSettingsExpanded}
        onChange={() => setIsExportSettingsExpanded(!isExportSettingsExpanded)}
        sx={{
          '&:before': { display: 'none' },
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          width: '100%',
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMore sx={{ color: theme.palette.colors?.primary?.[600] }} />
          }
          sx={{
            px: 0,
            minHeight: 'auto',
            width: 'fit-content',
            backgroundColor: 'transparent !important',
            '& .MuiAccordionSummary-content': { my: 0.5, flexGrow: 0, mr: 1 },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: theme.palette.colors?.primary?.[600],
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {isExportSettingsExpanded
              ? 'Hide Export Settings'
              : 'Show Export Settings'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Stack direction="row" gap={4} sx={{ width: '100%' }}>
            <Controller
              name="matterRegion"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ width: '50%' }} size="small">
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Data Region
                  </Typography>
                  <TextField {...field} select>
                    {VAULT_EXPORT_REGION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              )}
            />
            <Controller
              name="exportFormat"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ width: '50%' }} size="small">
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Format
                  </Typography>
                  <TextField {...field} select>
                    {exportFormatOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              )}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </>
  );
}
